/**
 * ProceduresAdminService
 * Console surface of Procedures Management: dashboard, procedures (details, content, publish, versions,
 * QR), media, access, reference documents, chat questions, AI imports, runs (+ CSV export), problems,
 * categories and settings.
 * Route: procedures/api/procedures/admin (roles Admin,ProceduresAdmin,ProceduresEditor + per-action rights,
 * see PROCEDURES_RIGHTS)
 *
 * Uploads (media, documents, import sources): presign -> `uploadToPresignedUrl(presign, file)` -> confirm.
 * Videos go through File Management (domain PROCEDURES_FILE_DOMAIN, recordId = procedureID) and are then
 * attached with `attachVideo`.
 */
import { BaseService } from '../base/BaseService';
import { unwrapProceduresResponse as unwrap } from './unwrapProceduresResponse';
/** Procedures console service (JWT + role + rights). */
export class ProceduresAdminService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ProceduresAdminService.DEFAULT_SERVICE_PATH,
            basePath: ProceduresAdminService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    static okOrSuccess(response) {
        return response.data ?? response.success;
    }
    static runListParams(query = {}) {
        return {
            procedureID: query.procedureID,
            statusID: query.statusID,
            channelID: query.channelID,
            ministryID: query.ministryID,
            hasProblems: query.hasProblems,
            search: query.search,
            from: query.from,
            to: query.to,
            page: query.page,
            pageSize: query.pageSize,
        };
    }
    // ---- dashboard ----
    /** GET api/procedures/admin/dashboard (Read) */
    async getDashboard() {
        return unwrap(await this.getDashboardRaw(), 'Procedures dashboard not found');
    }
    getDashboardRaw() {
        return this.http.get(this.buildUrl('dashboard'));
    }
    // ---- procedures ----
    /** GET api/procedures/admin/procedures (Read) */
    async listProcedures(query = {}) {
        return unwrap(await this.listProceduresRaw(query), 'Procedures not found');
    }
    listProceduresRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('procedures', {
            search: query.search,
            categoryID: query.categoryID,
            includeSubcategories: query.includeSubcategories,
            locationID: query.locationID,
            tag: query.tag,
            visibilityID: query.visibilityID,
            statusID: query.statusID,
            collaboratorUserId: query.collaboratorUserId,
            reviewDue: query.reviewDue,
            includeArchived: query.includeArchived,
            sort: query.sort,
            page: query.page,
            pageSize: query.pageSize,
        }));
    }
    /** POST api/procedures/admin/procedures (Manage): create a draft. */
    async createProcedure(request) {
        return unwrap(await this.createProcedureRaw(request), 'Failed to create the procedure');
    }
    createProcedureRaw(request) {
        return this.http.post(this.buildUrl('procedures'), request);
    }
    /** GET api/procedures/admin/procedures/{id} (Read): the draft. */
    async getProcedure(procedureId) {
        return unwrap(await this.getProcedureRaw(procedureId), `Procedure ${procedureId} not found`);
    }
    getProcedureRaw(procedureId) {
        return this.http.get(this.buildUrl(`procedures/${procedureId}`));
    }
    /** PUT api/procedures/admin/procedures/{id} (Manage): details tab. */
    async updateProcedure(procedureId, request) {
        return unwrap(await this.updateProcedureRaw(procedureId, request), 'Failed to save the procedure');
    }
    updateProcedureRaw(procedureId, request) {
        return this.http.put(this.buildUrl(`procedures/${procedureId}`), request);
    }
    /** PUT api/procedures/admin/procedures/{id}/content (Manage): replace the whole draft content (steps, callouts, media order). */
    async saveProcedureContent(procedureId, request) {
        return unwrap(await this.saveProcedureContentRaw(procedureId, request), 'Failed to save the procedure content');
    }
    saveProcedureContentRaw(procedureId, request) {
        return this.http.put(this.buildUrl(`procedures/${procedureId}/content`), request);
    }
    /** DELETE api/procedures/admin/procedures/{id} (Manage): soft delete. */
    async deleteProcedure(procedureId) {
        return ProceduresAdminService.okOrSuccess(await this.deleteProcedureRaw(procedureId));
    }
    deleteProcedureRaw(procedureId) {
        return this.http.delete(this.buildUrl(`procedures/${procedureId}`));
    }
    /** POST api/procedures/admin/procedures/{id}/duplicate (Manage): copy as a new draft. */
    async duplicateProcedure(procedureId) {
        return unwrap(await this.duplicateProcedureRaw(procedureId), 'Failed to duplicate the procedure');
    }
    duplicateProcedureRaw(procedureId) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/duplicate`), {});
    }
    /** POST api/procedures/admin/procedures/{id}/publish (Publish): snapshot the draft as a new version. */
    async publishProcedure(procedureId, request = {}) {
        return unwrap(await this.publishProcedureRaw(procedureId, request), 'Failed to publish the procedure');
    }
    publishProcedureRaw(procedureId, request = {}) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/publish`), request);
    }
    /** POST api/procedures/admin/procedures/{id}/archive (Publish) */
    async archiveProcedure(procedureId) {
        return unwrap(await this.archiveProcedureRaw(procedureId), 'Failed to archive the procedure');
    }
    archiveProcedureRaw(procedureId) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/archive`), {});
    }
    /** POST api/procedures/admin/procedures/{id}/restore (Publish): bring an archived procedure back. */
    async restoreProcedure(procedureId) {
        return unwrap(await this.restoreProcedureRaw(procedureId), 'Failed to restore the procedure');
    }
    restoreProcedureRaw(procedureId) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/restore`), {});
    }
    /** POST api/procedures/admin/procedures/{id}/open-questions/dismiss (Manage): clear the AI import's open questions. */
    async dismissOpenQuestions(procedureId) {
        return unwrap(await this.dismissOpenQuestionsRaw(procedureId), 'Failed to dismiss the open questions');
    }
    dismissOpenQuestionsRaw(procedureId) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/open-questions/dismiss`), {});
    }
    /** POST api/procedures/admin/procedures/bulk (Manage): archive | restore | move. */
    async bulkProcedureAction(request) {
        return unwrap(await this.bulkProcedureActionRaw(request), 'Bulk action failed');
    }
    bulkProcedureActionRaw(request) {
        return this.http.post(this.buildUrl('procedures/bulk'), request);
    }
    /** GET api/procedures/admin/procedures/{id}/preview (Read): the portal view built from the draft. */
    async previewProcedure(procedureId) {
        return unwrap(await this.previewProcedureRaw(procedureId), `Procedure ${procedureId} preview not available`);
    }
    previewProcedureRaw(procedureId) {
        return this.http.get(this.buildUrl(`procedures/${procedureId}/preview`));
    }
    // ---- versions ----
    /** GET api/procedures/admin/procedures/{id}/versions (Read) */
    async getProcedureVersions(procedureId) {
        return (await this.getProcedureVersionsRaw(procedureId)).data ?? [];
    }
    getProcedureVersionsRaw(procedureId) {
        return this.http.get(this.buildUrl(`procedures/${procedureId}/versions`));
    }
    /** POST api/procedures/admin/procedures/{id}/versions/{versionId}/restore (Manage): replace the draft with that version. */
    async restoreProcedureVersion(procedureId, versionId) {
        return unwrap(await this.restoreProcedureVersionRaw(procedureId, versionId), 'Failed to restore the version');
    }
    restoreProcedureVersionRaw(procedureId, versionId) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/versions/${versionId}/restore`), {});
    }
    // ---- QR ----
    /** GET api/procedures/admin/procedures/{id}/qr (Read) */
    async getProcedureQr(procedureId) {
        return unwrap(await this.getProcedureQrRaw(procedureId), 'QR code not found');
    }
    getProcedureQrRaw(procedureId) {
        return this.http.get(this.buildUrl(`procedures/${procedureId}/qr`));
    }
    /** POST api/procedures/admin/procedures/{id}/qr/regenerate (Manage) */
    async regenerateProcedureQr(procedureId) {
        return unwrap(await this.regenerateProcedureQrRaw(procedureId), 'Failed to regenerate the QR code');
    }
    regenerateProcedureQrRaw(procedureId) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/qr/regenerate`), {});
    }
    /** GET api/procedures/admin/qr-sheet?categoryId=&locationId=&ids=1,2 (Read): printable QR cards (published only). */
    async getQrSheet(query = {}) {
        return (await this.getQrSheetRaw(query)).data ?? [];
    }
    getQrSheetRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('qr-sheet', {
            categoryId: query.categoryId,
            locationId: query.locationId,
            ids: query.ids && query.ids.length > 0 ? query.ids.join(',') : undefined,
        }));
    }
    // ---- media ----
    /** POST api/procedures/admin/procedures/{id}/media/presign (Manage): signed PUT for an image / audio / document. */
    async presignMedia(procedureId, request) {
        return unwrap(await this.presignMediaRaw(procedureId, request), 'Failed to start the upload');
    }
    presignMediaRaw(procedureId, request) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/media/presign`), request);
    }
    /** POST api/procedures/admin/procedures/{id}/media (Manage): confirm an uploaded file (images are resized server-side). */
    async confirmMedia(procedureId, request) {
        return unwrap(await this.confirmMediaRaw(procedureId, request), 'Failed to save the media');
    }
    confirmMediaRaw(procedureId, request) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/media`), request);
    }
    /** POST api/procedures/admin/procedures/{id}/media/video (Manage): attach a File Management video by mediaFileId. */
    async attachVideo(procedureId, request) {
        return unwrap(await this.attachVideoRaw(procedureId, request), 'Failed to attach the video');
    }
    attachVideoRaw(procedureId, request) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/media/video`), request);
    }
    /** PUT api/procedures/admin/procedures/{id}/media/{mediaId} (Manage): caption / alt text. */
    async updateMedia(procedureId, mediaId, request) {
        return unwrap(await this.updateMediaRaw(procedureId, mediaId, request), 'Failed to update the media');
    }
    updateMediaRaw(procedureId, mediaId, request) {
        return this.http.put(this.buildUrl(`procedures/${procedureId}/media/${mediaId}`), request);
    }
    /** DELETE api/procedures/admin/procedures/{id}/media/{mediaId} (Manage) */
    async deleteMedia(procedureId, mediaId) {
        return ProceduresAdminService.okOrSuccess(await this.deleteMediaRaw(procedureId, mediaId));
    }
    deleteMediaRaw(procedureId, mediaId) {
        return this.http.delete(this.buildUrl(`procedures/${procedureId}/media/${mediaId}`));
    }
    // ---- access ----
    /** GET api/procedures/admin/procedures/{id}/access (Read): audience + collaborators. */
    async getAccess(procedureId) {
        return unwrap(await this.getAccessRaw(procedureId), 'Procedure access not found');
    }
    getAccessRaw(procedureId) {
        return this.http.get(this.buildUrl(`procedures/${procedureId}/access`));
    }
    /** PUT api/procedures/admin/procedures/{id}/access (Manage + Owner or full access): replace audience + collaborators. */
    async saveAccess(procedureId, request) {
        return unwrap(await this.saveAccessRaw(procedureId, request), 'Failed to save access');
    }
    saveAccessRaw(procedureId, request) {
        return this.http.put(this.buildUrl(`procedures/${procedureId}/access`), request);
    }
    /**
     * GET api/procedures/admin/people?search=&take= (Manage): members for the People audience picker.
     * Search needs at least 2 characters; take is 1 to 50 (default 20).
     */
    async searchPeople(search, take = 20) {
        return (await this.searchPeopleRaw(search, take)).data ?? [];
    }
    searchPeopleRaw(search, take = 20) {
        return this.http.get(this.buildUrlWithParams('people', { search, take }));
    }
    /** GET api/procedures/admin/people/by-ids?ids=1,2 (Manage): members by MemberID (max 200). */
    async getPeopleByIds(ids) {
        if (ids.length === 0)
            return [];
        return (await this.getPeopleByIdsRaw(ids)).data ?? [];
    }
    getPeopleByIdsRaw(ids) {
        return this.http.get(this.buildUrlWithParams('people/by-ids', { ids: ids.join(',') }));
    }
    // ---- reference documents ----
    /** GET api/procedures/admin/procedures/{id}/documents (Read) */
    async getDocuments(procedureId) {
        return (await this.getDocumentsRaw(procedureId)).data ?? [];
    }
    getDocumentsRaw(procedureId) {
        return this.http.get(this.buildUrl(`procedures/${procedureId}/documents`));
    }
    /** POST api/procedures/admin/procedures/{id}/documents/presign (Manage) */
    async presignDocument(procedureId, request) {
        return unwrap(await this.presignDocumentRaw(procedureId, request), 'Failed to start the upload');
    }
    presignDocumentRaw(procedureId, request) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/documents/presign`), request);
    }
    /** POST api/procedures/admin/procedures/{id}/documents (Manage): confirm an uploaded document (queued for AI indexing). */
    async confirmDocument(procedureId, request) {
        return unwrap(await this.confirmDocumentRaw(procedureId, request), 'Failed to save the document');
    }
    confirmDocumentRaw(procedureId, request) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/documents`), request);
    }
    /** PUT api/procedures/admin/procedures/{id}/documents/{documentId} (Manage): public / private. */
    async updateDocument(procedureId, documentId, request) {
        return unwrap(await this.updateDocumentRaw(procedureId, documentId, request), 'Failed to update the document');
    }
    updateDocumentRaw(procedureId, documentId, request) {
        return this.http.put(this.buildUrl(`procedures/${procedureId}/documents/${documentId}`), request);
    }
    /** POST api/procedures/admin/procedures/{id}/documents/{documentId}/reindex (Manage) */
    async reindexDocument(procedureId, documentId) {
        return unwrap(await this.reindexDocumentRaw(procedureId, documentId), 'Failed to reindex the document');
    }
    reindexDocumentRaw(procedureId, documentId) {
        return this.http.post(this.buildUrl(`procedures/${procedureId}/documents/${documentId}/reindex`), {});
    }
    /** DELETE api/procedures/admin/procedures/{id}/documents/{documentId} (Manage) */
    async deleteDocument(procedureId, documentId) {
        return ProceduresAdminService.okOrSuccess(await this.deleteDocumentRaw(procedureId, documentId));
    }
    deleteDocumentRaw(procedureId, documentId) {
        return this.http.delete(this.buildUrl(`procedures/${procedureId}/documents/${documentId}`));
    }
    // ---- chat questions ----
    /** GET api/procedures/admin/procedures/{id}/questions?page=&pageSize= (RunsRead): user questions with askedBy. */
    async getProcedureQuestions(procedureId, query = {}) {
        return unwrap(await this.getProcedureQuestionsRaw(procedureId, query), 'Questions not found');
    }
    getProcedureQuestionsRaw(procedureId, query = {}) {
        return this.http.get(this.buildUrlWithParams(`procedures/${procedureId}/questions`, { page: query.page, pageSize: query.pageSize }));
    }
    // ---- AI imports ----
    /** POST api/procedures/admin/imports/presign (Import): signed PUT for an import source file. */
    async presignImport(request) {
        return unwrap(await this.presignImportRaw(request), 'Failed to start the upload');
    }
    presignImportRaw(request) {
        return this.http.post(this.buildUrl('imports/presign'), request);
    }
    /** POST api/procedures/admin/imports (Import): queue an AI import; poll getImport every 2 s until Ready / Failed. */
    async startImport(request) {
        return unwrap(await this.startImportRaw(request), 'Failed to start the import');
    }
    startImportRaw(request) {
        return this.http.post(this.buildUrl('imports'), request);
    }
    /** GET api/procedures/admin/imports?page=&pageSize= (Import) */
    async listImports(query = {}) {
        return unwrap(await this.listImportsRaw(query), 'Imports not found');
    }
    listImportsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('imports', { page: query.page, pageSize: query.pageSize }));
    }
    /** GET api/procedures/admin/imports/{importId} (Import) */
    async getImport(importId) {
        return unwrap(await this.getImportRaw(importId), `Import ${importId} not found`);
    }
    getImportRaw(importId) {
        return this.http.get(this.buildUrl(`imports/${importId}`));
    }
    // ---- runs ----
    /** GET api/procedures/admin/runs (RunsRead) */
    async listRuns(query = {}) {
        return unwrap(await this.listRunsRaw(query), 'Runs not found');
    }
    listRunsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('runs', ProceduresAdminService.runListParams(query)));
    }
    /** GET api/procedures/admin/runs/export (RunsRead): the filtered runs as raw text/csv. */
    exportRunsCsv(query = {}) {
        return this.http.getBlob(this.buildUrlWithParams('runs/export', ProceduresAdminService.runListParams(query)));
    }
    /** GET api/procedures/admin/runs/{runId} (RunsRead): run + chat transcript. */
    async getRun(runId) {
        return unwrap(await this.getRunRaw(runId), `Run ${runId} not found`);
    }
    getRunRaw(runId) {
        return this.http.get(this.buildUrl(`runs/${runId}`));
    }
    // ---- problems ----
    /** GET api/procedures/admin/problems?open=&procedureId=&page=&pageSize= (RunsRead) */
    async listProblems(query = {}) {
        return unwrap(await this.listProblemsRaw(query), 'Problems not found');
    }
    listProblemsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('problems', {
            open: query.open,
            procedureId: query.procedureId,
            page: query.page,
            pageSize: query.pageSize,
        }));
    }
    /** POST api/procedures/admin/problems/{runId}/{stepId}/resolve (Manage) */
    async resolveProblem(runId, stepId, request = {}) {
        return unwrap(await this.resolveProblemRaw(runId, stepId, request), 'Failed to resolve the problem');
    }
    resolveProblemRaw(runId, stepId, request = {}) {
        return this.http.post(this.buildUrl(`problems/${runId}/${stepId}/resolve`), request);
    }
    // ---- categories ----
    /** GET api/procedures/admin/categories (Read) */
    async getCategories() {
        return (await this.getCategoriesRaw()).data ?? [];
    }
    getCategoriesRaw() {
        return this.http.get(this.buildUrl('categories'));
    }
    /** POST api/procedures/admin/categories (SettingsManage) */
    async createCategory(request) {
        return unwrap(await this.createCategoryRaw(request), 'Failed to create the category');
    }
    createCategoryRaw(request) {
        return this.http.post(this.buildUrl('categories'), request);
    }
    /** PUT api/procedures/admin/categories/{categoryId} (SettingsManage) */
    async updateCategory(categoryId, request) {
        return unwrap(await this.updateCategoryRaw(categoryId, request), 'Failed to save the category');
    }
    updateCategoryRaw(categoryId, request) {
        return this.http.put(this.buildUrl(`categories/${categoryId}`), request);
    }
    /** DELETE api/procedures/admin/categories/{categoryId} (SettingsManage): refused while in use. */
    async deleteCategory(categoryId) {
        return ProceduresAdminService.okOrSuccess(await this.deleteCategoryRaw(categoryId));
    }
    deleteCategoryRaw(categoryId) {
        return this.http.delete(this.buildUrl(`categories/${categoryId}`));
    }
    // ---- settings ----
    /** GET api/procedures/admin/settings (Read) */
    async getSettings() {
        return unwrap(await this.getSettingsRaw(), 'Procedures settings not found');
    }
    getSettingsRaw() {
        return this.http.get(this.buildUrl('settings'));
    }
    /** PUT api/procedures/admin/settings (SettingsManage) */
    async saveSettings(request) {
        return unwrap(await this.saveSettingsRaw(request), 'Failed to save the settings');
    }
    saveSettingsRaw(request) {
        return this.http.put(this.buildUrl('settings'), request);
    }
}
ProceduresAdminService.DEFAULT_SERVICE_PATH = 'procedures';
ProceduresAdminService.BASE_PATH = 'api/procedures/admin';
//# sourceMappingURL=ProceduresAdminService.js.map